import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import {
  Users, Baby, Camera, Calendar,
  Loader2, Plus, Utensils, TrendingUp, MessageSquare, Send,
  Check, X, UserCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import EmptyState from '@/components/ui/EmptyState';
import StatsCard from '@/components/ui/StatsCard';
import NewAnnouncementDialog from '@/components/NewAnnouncementDialog';
import PhotoUploadDialog from '@/components/PhotoUploadDialog';
import SpeechToTextButton from '@/components/SpeechToTextButton';
import { useAuth } from '@/lib/AuthContext';
import { useTheme } from '@/lib/ThemeContext';
import { useBranch } from '@/hooks/useBranches';
import { useEnabledModulesForUser } from '@/hooks/useBranchModules';
import { useSendMessage } from '@/hooks/useMessages';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { isWeekend } from '@/lib/dateUtils';

// Helper function to adjust color brightness for gradient
const adjustColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

export default function TeacherDashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [secondaryGroups, setSecondaryGroups] = useState([]);
  const [children, setChildren] = useState([]);
  const [groupStats, setGroupStats] = useState({});
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  // New conversation dialog states
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [conversationSubject, setConversationSubject] = useState('');
  const [conversationMessage, setConversationMessage] = useState('');
  const [childrenInGroups, setChildrenInGroups] = useState([]);
  const [sending, setSending] = useState(false);

  const sendMessage = useSendMessage();

  const branchId = userProfile?.current_branch_id || userProfile?.branch_ids?.[0];
  const { data: branch } = useBranch(branchId, { enabled: !!branchId });
  const { isModuleEnabled } = useEnabledModulesForUser();
  const chatEnabled = isModuleEnabled('chat');
  const mediaEnabled = isModuleEnabled('media');
  const { getActiveColors } = useTheme();
  const themeColors = getActiveColors();

  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (userProfile) {
      loadData();
    }
  }, [userProfile]);

  // Default criteria schema (must match DailyUpdates.jsx fallback keys!)
  const DEFAULT_CRITERIA_SCHEMA = [
    { key: 'mood', label: 'Nastrój', icon: '😊' },
    { key: 'eating', label: 'Jedzenie', icon: '🍽️' },
    { key: 'nap', label: 'Sen', icon: '😴' }
  ];

  const loadData = async () => {
    if (!userProfile) return;

    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      if (!branchId) {
        setLoading(false);
        return;
      }

      // Use optimized RPC function that combines all 6 queries into one
      const { data: result, error } = await supabase.rpc('get_teacher_dashboard_full', {
        p_teacher_id: userProfile.id,
        p_query_date: today
      });

      if (error) throw error;

      const branchSettings = result?.branchSettings || {};
      // Closure from RPC (branch settings + holidays) or weekend — no journal data expected
      const isClosureDay = result?.isClosureDay || isWeekend(today);
      const mainGroups = result?.mainGroups || [];
      const secondaryTeacherGroups = result?.secondaryGroups || [];
      const childrenData = result?.children || [];
      const updatesData = result?.dailyUpdates || [];
      const absencesData = result?.absences || [];

      // Get criteria schema from branch settings
      let criteriaSchema = DEFAULT_CRITERIA_SCHEMA;
      if (branchSettings?.daily_update_schema?.length > 0) {
        criteriaSchema = branchSettings.daily_update_schema;
      }

      logger.debug('Teacher Dashboard - Group Analysis:', {
        mainGroupsCount: mainGroups.length,
        secondaryGroupsCount: secondaryTeacherGroups.length
      });

      const allTeacherGroups = [...mainGroups, ...secondaryTeacherGroups];

      if (allTeacherGroups.length === 0) {
        setGroups([]);
        setSecondaryGroups([]);
        setChildren([]);
        setLoading(false);
        return;
      }

      // Create a set of absent child IDs for quick lookup
      const absentChildIds = new Set((absencesData || []).map(a => a.child_id));

      setGroups(mainGroups);
      setSecondaryGroups(secondaryTeacherGroups);
      setChildren(childrenData || []);

      // Calculate stats per group
      const stats = {};
      const numCriteria = criteriaSchema.length;

      allTeacherGroups.forEach(group => {
        const groupChildren = (childrenData || []).filter(c => c.group_id === group.id);
        const groupUpdates = (updatesData || []).filter(u => u.group_id === group.id);

        // If it's a closure day, everything is 100% complete
        if (isClosureDay) {
          stats[group.id] = {
            totalChildren: groupChildren.length,
            presentChildren: 0,
            updatedChildren: 0,
            completionPercent: 100,
            isClosureDay: true
          };
          return;
        }

        // Filter out children with reported absences - they don't count toward completion
        const eligibleChildren = groupChildren.filter(c => !absentChildIds.has(c.id));
        const eligibleChildIds = new Set(eligibleChildren.map(c => c.id));

        // For eligible children, calculate completion based on all fields
        // Total fields per child = 1 (attendance) + number of criteria
        const fieldsPerChild = 1 + numCriteria;
        const totalFields = eligibleChildren.length * fieldsPerChild;

        let filledFields = 0;

        eligibleChildren.forEach(child => {
          const update = groupUpdates.find(u => u.child_id === child.id);

          if (update) {
            // Count attendance field (if marked present or absent)
            if (update.attendance === true || update.attendance === false) {
              filledFields += 1;
            }

            // If absent (attendance === false): count as complete (no criteria needed)
            // If present (attendance === true): count actual filled criteria
            if (update.attendance === false) {
              // Absent children are considered complete - add all criteria fields
              filledFields += numCriteria;
            } else if (update.attendance === true && update.criteria_values) {
              criteriaSchema.forEach(criterion => {
                if (update.criteria_values[criterion.key]) {
                  filledFields += 1;
                }
              });
            }
          }
        });

        // Count children marked as present (for display)
        const presentCount = groupUpdates.filter(u =>
          eligibleChildIds.has(u.child_id) && u.attendance === true
        ).length;

        stats[group.id] = {
          totalChildren: groupChildren.length,
          eligibleChildren: eligibleChildren.length,
          absentChildren: groupChildren.length - eligibleChildren.length,
          presentChildren: presentCount,
          updatedChildren: filledFields,
          totalFields: totalFields,
          completionPercent: totalFields > 0
            ? Math.round((filledFields / totalFields) * 100)
            : (eligibleChildren.length === 0 ? 100 : 0), // 100% if all children are absent
          isClosureDay: false
        };
      });

      setGroupStats(stats);

      // Use already-loaded children data for conversation dialog
      setChildrenInGroups(childrenData || []);
    } catch (e) {
      logger.error('Failed to load data:', e);
      toast.error('Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  };

  // Get group name by ID
  const getGroupName = (groupId) => {
    const allGroups = [...groups, ...secondaryGroups];
    const group = allGroups.find(g => g.id === groupId);
    return group?.name || 'Nieznana grupa';
  };

  // Start new conversation
  const handleStartConversation = async () => {
    if (!selectedChildId || !conversationMessage.trim()) return;

    setSending(true);
    try {
      const child = childrenInGroups.find(c => c.id === selectedChildId);
      if (!child) throw new Error('Nie znaleziono dziecka');

      // Get child's parents
      const { data: childData } = await supabase
        .from('children')
        .select('parent_ids, branch_id')
        .eq('id', selectedChildId)
        .single();

      // Create conversation using RPC function (bypasses slow RLS)
      const { data: conversationId, error: convError } = await supabase.rpc('create_teacher_conversation', {
        p_branch_id: childData?.branch_id || userProfile.current_branch_id || userProfile.branch_ids?.[0],
        p_group_id: child.group_id,
        p_child_id: child.id,
        p_parent_id: childData?.parent_ids?.[0] || null,
        p_subject: conversationSubject.trim() || `Wiadomość dot. ${child.first_name}`
      });

      if (convError) throw convError;

      // Send first message using RPC function (bypasses slow RLS)
      const { error: msgError } = await supabase.rpc('send_conversation_message', {
        p_conversation_id: conversationId,
        p_content: conversationMessage.trim()
      });

      if (msgError) throw msgError;

      toast.success('Wiadomość została wysłana');
      setNewConversationOpen(false);
      setSelectedChildId('');
      setConversationSubject('');
      setConversationMessage('');
    } catch (e) {
      logger.error('Failed to start conversation:', e);
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (groups.length === 0 && secondaryGroups.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="Nie przypisano grup"
        description="Skontaktuj się z administratorem, aby zostać przypisanym do grup"
      />
    );
  }

  const totalChildren = children.length;

  // Calculate overall completion from group stats
  // Sum up filled fields and total fields across all groups
  const allGroupIds = [...groups, ...secondaryGroups].map(g => g.id);
  let totalFilledFields = 0;
  let totalRequiredFields = 0;
  let isAnyClosureDay = false;

  allGroupIds.forEach(groupId => {
    const stats = groupStats[groupId];
    if (stats) {
      if (stats.isClosureDay) {
        isAnyClosureDay = true;
      } else {
        totalFilledFields += stats.updatedChildren || 0;
        totalRequiredFields += stats.totalFields || 0;
      }
    }
  });

  const overallCompletion = isAnyClosureDay
    ? 100
    : (totalRequiredFields > 0
      ? Math.round((totalFilledFields / totalRequiredFields) * 100)
      : (totalChildren === 0 ? 0 : 100)); // 100% if all children have absences

  // Count present children across all groups for the summary
  const presentChildren = allGroupIds.reduce((sum, groupId) => {
    const stats = groupStats[groupId];
    return sum + (stats?.presentChildren || 0);
  }, 0);

  // Count absent children across all groups
  const absentChildren = allGroupIds.reduce((sum, groupId) => {
    const stats = groupStats[groupId];
    return sum + (stats?.absentChildren || 0);
  }, 0);

  const totalGroups = groups.length + secondaryGroups.length;

  const teachersCanEditMenu = branch?.settings?.teachers_can_edit_menu || false;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pulpit Nauczyciela</h1>
          <p className="text-slate-500">{format(new Date(), 'EEEE, d MMMM yyyy', { locale: pl })}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {teachersCanEditMenu && (
            <Button
              onClick={() => navigate('/TeacherMenu')}
              className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition-opacity duration-200 ease-in-out hover:opacity-90"
            >
              <Utensils className="w-4 h-4" />
              Dodaj Jadłospis
            </Button>
          )}
          {chatEnabled && (
            <Button
              className={cn(
                "gap-2 text-white transition-opacity duration-200 ease-in-out hover:opacity-90",
                themeColors.headerBg
              )}
              onClick={() => setNewConversationOpen(true)}
            >
              <MessageSquare className="w-4 h-4" />
              Nowa rozmowa
            </Button>
          )}
          <Button
            className={cn(
              "gap-2 text-white transition-opacity duration-200 ease-in-out hover:opacity-90",
              themeColors.headerBg
            )}
            onClick={() => setAnnouncementDialogOpen(true)}
          >
            <Plus className="w-4 h-4" />
            Nowe ogłoszenie
          </Button>
          {mediaEnabled && (
            <Button
              className={cn(
                "gap-2 text-white transition-opacity duration-200 ease-in-out hover:opacity-90",
                themeColors.headerBg
              )}
              onClick={() => setPhotoDialogOpen(true)}
            >
              <Camera className="w-4 h-4" />
              Dodaj zdjęcie
            </Button>
          )}
        </div>
      </div>

      {/* New Announcement Dialog */}
      <NewAnnouncementDialog
        open={announcementDialogOpen}
        onOpenChange={setAnnouncementDialogOpen}
      />

      {/* Photo Upload Dialog */}
      <PhotoUploadDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <StatsCard
          title="Moje grupy"
          value={totalGroups}
          icon={Users}
          gradient="from-blue-500 to-indigo-600"
        />
        <StatsCard
          title="Dzieci"
          value={totalChildren}
          icon={Baby}
          gradient="from-pink-500 to-rose-600"
        />
        <div className="col-span-2 md:col-span-1">
          <StatsCard
            title="Kompletność na dziś"
            value={isAnyClosureDay ? 'Dzień wolny' : `${overallCompletion}%`}
            icon={TrendingUp}
            gradient="from-green-500 to-emerald-600"
            trend={isAnyClosureDay
              ? 'Brak wpisów — placówka nie pracuje'
              : absentChildren > 0
                ? `${presentChildren} obecnych, ${absentChildren} nieobecnych`
                : `${presentChildren} z ${totalChildren} dzieci obecnych`
            }
          />
        </div>
      </div>

      {/* Twoje grupy & Twoje dodatkowe grupy — side by side on desktop, stack when not enough space */}
      <div
        className={cn(
          'grid gap-4 items-start',
          groups.length > 0 && secondaryGroups.length > 0
            ? 'grid-cols-1 xl:grid-cols-2'
            : 'grid-cols-1'
        )}
      >
        {/* Main Groups Cards */}
        {groups.length > 0 && (
          <div className="min-w-0 @container">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Twoje grupy</h2>
            <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2 @2xl:grid-cols-3">
            {groups.map(group => {
              const stats = groupStats[group.id] || { totalChildren: 0, presentChildren: 0, updatedChildren: 0, completionPercent: 0, isClosureDay: false, absentChildren: 0 };
              const groupColor = group.color_scheme || '#6366f1';

              return (
                <Card
                  key={group.id}
                  className="hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden"
                >
                  {/* Gradient Header with Group Info */}
                  <CardHeader
                    className="pb-4 relative"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${groupColor}, ${adjustColor(groupColor, -20)})`,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Group Avatar/Photo */}
                      {group.picture_url ? (
                        <img
                          src={group.picture_url}
                          alt={group.name}
                          className="w-16 h-16 rounded-2xl object-cover border-2 border-white/30 shadow-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
                          <Users className="w-8 h-8 text-white" />
                        </div>
                      )}

                      {/* Group Name and Stats */}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-white text-lg mb-1 drop-shadow-md">
                          {group.name}
                        </CardTitle>
                        <p className="text-xs text-white/80">{stats.totalChildren} dzieci</p>

                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {stats.isClosureDay && (
                            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 text-xs">
                              🌴 Dzień wolny
                            </Badge>
                          )}
                          {stats.completionPercent === 100 && !stats.isClosureDay && (
                            <Badge className="bg-green-500 text-white border-0 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Kompletne
                            </Badge>
                          )}
                          {stats.absentChildren > 0 && (
                            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 text-xs">
                              {stats.absentChildren} nieobecnych
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4">
                    {/* Icon-Enhanced Stats */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Baby className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Dzieci</p>
                          <p className="text-lg font-bold text-slate-900">{stats.totalChildren}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <UserCheck className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Obecni</p>
                          <p className="text-lg font-bold text-slate-900">{stats.presentChildren}</p>
                        </div>
                      </div>
                      {stats.absentChildren > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <X className="w-4 h-4 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Nieobecni</p>
                            <p className="text-lg font-bold text-slate-900">{stats.absentChildren}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Color-Coded Progress Bar (or closure message) */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 font-medium">Postęp dziennika</span>
                        {stats.isClosureDay ? (
                          <span className="font-medium text-slate-500">Dzień wolny</span>
                        ) : (
                          <span className={cn(
                            "font-bold",
                            stats.completionPercent === 100 ? "text-green-600" :
                            stats.completionPercent >= 75 ? "text-blue-600" :
                            stats.completionPercent >= 50 ? "text-amber-600" :
                            "text-red-600"
                          )}>
                            {stats.completionPercent}%
                          </span>
                        )}
                      </div>
                      {stats.isClosureDay ? (
                        <p className="text-xs text-slate-500 py-1">Nie prowadzimy dziennika tego dnia</p>
                      ) : (
                        <Progress
                          value={stats.completionPercent}
                          className={cn(
                            "h-2.5",
                            stats.completionPercent === 100 ? "[&>div]:bg-green-500" :
                            stats.completionPercent >= 75 ? "[&>div]:bg-blue-500" :
                            stats.completionPercent >= 50 ? "[&>div]:bg-amber-500" :
                            "[&>div]:bg-red-500"
                          )}
                        />
                      )}
                    </div>

                    {/* Improved Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        className="flex-1 gap-2 h-11 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white border-0 shadow-md hover:shadow-lg transition-all"
                        onClick={() => navigate(`/DailyUpdates?group=${group.id}`)}
                      >
                        <Calendar className="w-4 h-4" />
                        Dziennik
                      </Button>
                      {mediaEnabled && (
                        <Button
                          className="flex-1 gap-2 h-11 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 shadow-md hover:shadow-lg transition-all"
                          onClick={() => navigate(`/PhotosManagement?group=${group.id}`)}
                        >
                          <Camera className="w-4 h-4" />
                          Media
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            </div>
          </div>
        )}

        {/* Secondary Groups Cards */}
        {secondaryGroups.length > 0 && (
          <div className="min-w-0 @container">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              Twoje dodatkowe grupy
              <Badge variant="secondary" className="text-xs">Wsparcie</Badge>
            </h2>
            <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2 @2xl:grid-cols-3">
            {secondaryGroups.map(group => {
              const stats = groupStats[group.id] || { totalChildren: 0, presentChildren: 0, updatedChildren: 0, completionPercent: 0, isClosureDay: false, absentChildren: 0 };
              const groupColor = group.color_scheme || '#6366f1';

              return (
                <Card
                  key={group.id}
                  className="hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden border-dashed border-2"
                >
                  {/* Gradient Header with Group Info */}
                  <CardHeader
                    className="pb-4 relative"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${groupColor}, ${adjustColor(groupColor, -20)})`,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Group Avatar/Photo */}
                      {group.picture_url ? (
                        <img
                          src={group.picture_url}
                          alt={group.name}
                          className="w-16 h-16 rounded-2xl object-cover border-2 border-white/30 shadow-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
                          <Users className="w-8 h-8 text-white" />
                        </div>
                      )}

                      {/* Group Name and Stats */}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-white text-lg mb-1 drop-shadow-md">
                          {group.name}
                        </CardTitle>
                        <p className="text-xs text-white/80">{stats.totalChildren} dzieci</p>

                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {stats.isClosureDay && (
                            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 text-xs">
                              🌴 Dzień wolny
                            </Badge>
                          )}
                          {stats.completionPercent === 100 && !stats.isClosureDay && (
                            <Badge className="bg-green-500 text-white border-0 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Kompletne
                            </Badge>
                          )}
                          {stats.absentChildren > 0 && (
                            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 text-xs">
                              {stats.absentChildren} nieobecnych
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4">
                    {/* Icon-Enhanced Stats */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Baby className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Dzieci</p>
                          <p className="text-lg font-bold text-slate-900">{stats.totalChildren}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <UserCheck className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Obecni</p>
                          <p className="text-lg font-bold text-slate-900">{stats.presentChildren}</p>
                        </div>
                      </div>
                      {stats.absentChildren > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <X className="w-4 h-4 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Nieobecni</p>
                            <p className="text-lg font-bold text-slate-900">{stats.absentChildren}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Color-Coded Progress Bar (or closure message) */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 font-medium">Postęp dziennika</span>
                        {stats.isClosureDay ? (
                          <span className="font-medium text-slate-500">Dzień wolny</span>
                        ) : (
                          <span className={cn(
                            "font-bold",
                            stats.completionPercent === 100 ? "text-green-600" :
                            stats.completionPercent >= 75 ? "text-blue-600" :
                            stats.completionPercent >= 50 ? "text-amber-600" :
                            "text-red-600"
                          )}>
                            {stats.completionPercent}%
                          </span>
                        )}
                      </div>
                      {stats.isClosureDay ? (
                        <p className="text-xs text-slate-500 py-1">Nie prowadzimy dziennika tego dnia</p>
                      ) : (
                        <Progress
                          value={stats.completionPercent}
                          className={cn(
                            "h-2.5",
                            stats.completionPercent === 100 ? "[&>div]:bg-green-500" :
                            stats.completionPercent >= 75 ? "[&>div]:bg-blue-500" :
                            stats.completionPercent >= 50 ? "[&>div]:bg-amber-500" :
                            "[&>div]:bg-red-500"
                          )}
                        />
                      )}
                    </div>

                    {/* Improved Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        className="flex-1 gap-2 h-11 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white border-0 shadow-md hover:shadow-lg transition-all"
                        onClick={() => navigate(`/DailyUpdates?group=${group.id}`)}
                      >
                        <Calendar className="w-4 h-4" />
                        Dziennik
                      </Button>
                      {mediaEnabled && (
                        <Button
                          className="flex-1 gap-2 h-11 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 shadow-md hover:shadow-lg transition-all"
                          onClick={() => navigate(`/PhotosManagement?group=${group.id}`)}
                        >
                          <Camera className="w-4 h-4" />
                          Media
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={newConversationOpen} onOpenChange={setNewConversationOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nowa rozmowa z rodzicem</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Dotyczy dziecka *</Label>
              <Select
                value={selectedChildId}
                onValueChange={setSelectedChildId}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Wybierz dziecko" />
                </SelectTrigger>
                <SelectContent>
                  {childrenInGroups.map(child => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.first_name} {child.last_name}
                      <span className="text-slate-400 ml-2">
                        ({getGroupName(child.group_id)})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="conv_subject">Temat (opcjonalnie)</Label>
                <SpeechToTextButton
                  currentValue={conversationSubject}
                  onResult={setConversationSubject}
                  append={false}
                  size="icon"
                />
              </div>
              <Input
                id="conv_subject"
                value={conversationSubject}
                onChange={(e) => setConversationSubject(e.target.value)}
                placeholder="np. Informacja o zajęciach"
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="conv_message">Treść wiadomości *</Label>
                <SpeechToTextButton
                  currentValue={conversationMessage}
                  onResult={setConversationMessage}
                  append
                  size="icon"
                />
              </div>
              <Textarea
                id="conv_message"
                value={conversationMessage}
                onChange={(e) => setConversationMessage(e.target.value)}
                placeholder="Napisz swoją wiadomość..."
                rows={5}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewConversationOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleStartConversation}
              disabled={sending || !selectedChildId || !conversationMessage.trim()}
              className={cn("text-white", themeColors.headerBg)}
            >
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Wyślij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
